import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { 
  History, 
  RefreshCw, 
  Filter,
  User,
  Clock,
  FileText,
  CheckCircle,
  XCircle,
  Play,
  Trash2,
  Plus
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const actionIcons = {
  create: Plus,
  approve: CheckCircle,
  reject: XCircle,
  execute: Play,
  delete: Trash2,
  execute_ssh: Play,
  update_role: User
};

const actionColors = {
  create: "bg-blue-500/10 text-blue-400 border-blue-500/30",
  approve: "bg-green-500/10 text-green-400 border-green-500/30",
  reject: "bg-red-500/10 text-red-400 border-red-500/30",
  execute: "bg-purple-500/10 text-purple-400 border-purple-500/30",
  delete: "bg-red-500/10 text-red-400 border-red-500/30",
  execute_ssh: "bg-purple-500/10 text-purple-400 border-purple-500/30",
  update_role: "bg-yellow-500/10 text-yellow-400 border-yellow-500/30"
};

const roleColors = {
  admin: "bg-blue-500/10 text-blue-400 border-blue-500/30",
  senior_admin: "bg-yellow-500/10 text-yellow-400 border-yellow-500/30",
  junior_admin: "bg-green-500/10 text-green-400 border-green-500/30"
};

export default function AuditLogs() {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    resource_type: "",
    action: ""
  });

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.resource_type) params.append("resource_type", filters.resource_type);
      if (filters.action) params.append("action", filters.action);
      
      const response = await axios.get(`${API}/audit-logs?${params.toString()}`);
      setLogs(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Access denied. Senior admin or admin access required.");
      } else {
        toast.error("Failed to fetch audit logs");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [filters]);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString();
  };

  const formatDetails = (details) => {
    if (!details || Object.keys(details).length === 0) return null;
    return Object.entries(details)
      .filter(([key, value]) => value !== null && value !== undefined)
      .map(([key, value]) => `${key}: ${typeof value === 'object' ? JSON.stringify(value) : value}`)
      .join(', ');
  };

  // Check if user has access
  if (user && user.role === 'junior_admin') {
    return (
      <div className="p-6" data-testid="audit-logs-page">
        <Card className="bg-[#0c0c0e] border-white/5">
          <CardContent className="py-12 text-center">
            <History className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">Access Restricted</h3>
            <p className="text-zinc-400">Senior admin or admin access required to view audit logs.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="audit-logs-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-mono text-white tracking-tight">Audit Logs</h1>
          <p className="text-zinc-400 text-sm mt-1">Complete history of system actions and approvals</p>
        </div>
        <Button
          onClick={fetchLogs}
          variant="outline"
          className="bg-white/5 border-white/10 hover:bg-white/10 text-white"
          data-testid="refresh-logs-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-[#0c0c0e] border-white/5">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <Filter className="w-5 h-5 text-zinc-400" />
            <Select
              value={filters.resource_type}
              onValueChange={(value) => setFilters({ ...filters, resource_type: value === "all" ? "" : value })}
            >
              <SelectTrigger className="w-40 bg-black/50 border-white/10 text-white" data-testid="filter-resource">
                <SelectValue placeholder="All Resources" />
              </SelectTrigger>
              <SelectContent className="bg-[#0c0c0e] border-white/10">
                <SelectItem value="all">All Resources</SelectItem>
                <SelectItem value="approval">Approvals</SelectItem>
                <SelectItem value="server">Servers</SelectItem>
                <SelectItem value="ssh_credentials">SSH Credentials</SelectItem>
                <SelectItem value="user">Users</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={filters.action}
              onValueChange={(value) => setFilters({ ...filters, action: value === "all" ? "" : value })}
            >
              <SelectTrigger className="w-40 bg-black/50 border-white/10 text-white" data-testid="filter-action">
                <SelectValue placeholder="All Actions" />
              </SelectTrigger>
              <SelectContent className="bg-[#0c0c0e] border-white/10">
                <SelectItem value="all">All Actions</SelectItem>
                <SelectItem value="create">Create</SelectItem>
                <SelectItem value="approve">Approve</SelectItem>
                <SelectItem value="reject">Reject</SelectItem>
                <SelectItem value="execute">Execute</SelectItem>
                <SelectItem value="delete">Delete</SelectItem>
              </SelectContent>
            </Select>
            {(filters.resource_type || filters.action) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setFilters({ resource_type: "", action: "" })}
                className="text-zinc-400 hover:text-white"
              >
                Clear filters
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Logs List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 text-blue-400 animate-spin" />
        </div>
      ) : logs.length === 0 ? (
        <Card className="bg-[#0c0c0e] border-white/5">
          <CardContent className="py-12 text-center">
            <History className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No audit logs found</h3>
            <p className="text-zinc-400">Actions will be recorded here when users interact with the system.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {logs.map((log) => {
            const ActionIcon = actionIcons[log.action] || FileText;
            return (
              <Card key={log.id} className="bg-[#0c0c0e] border-white/5 hover:border-white/10 transition-colors">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${actionColors[log.action] || 'bg-zinc-500/10'}`}>
                        <ActionIcon className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={`border ${actionColors[log.action] || 'bg-zinc-500/10 text-zinc-400'}`}>
                            {log.action.toUpperCase()}
                          </Badge>
                          <Badge variant="outline" className="text-zinc-400 border-zinc-600">
                            {log.resource_type}
                          </Badge>
                          <Badge className={`border ${roleColors[log.user_role] || 'bg-zinc-500/10 text-zinc-400'}`}>
                            {log.user_role}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2 mt-2 text-sm text-zinc-300">
                          <User className="w-3 h-3" />
                          <span>{log.user_email}</span>
                        </div>
                        {formatDetails(log.details) && (
                          <p className="text-xs text-zinc-500 mt-2 font-mono bg-black/30 p-2 rounded">
                            {formatDetails(log.details)}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-zinc-500">
                      <Clock className="w-3 h-3" />
                      {formatDate(log.timestamp)}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
